import { useState } from "react";
import { Icon } from "@iconify/react";
import { Button } from "@/shared/components/button";
import { AppModal } from "@/shared/components/dialog";
import { CalendarDateField } from "@/shared/components/CalendarDateField";
import { IconTextField, IconSelectField } from "./FormFields";
import { createDependentApi } from "@/portals/patient/api/familyApi";
import { toastError, toastSuccess } from "@/shared/lib/toast";

const RELATIONSHIP_OPTIONS = ["Son", "Daughter", "Spouse", "Father", "Mother", "Other"];

interface AddFamilyMemberModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after the dependent profile is actually created on the backend, so the caller can refetch. */
  onCreated: () => void;
}

export function AddFamilyMemberModal({ open, onOpenChange, onCreated }: AddFamilyMemberModalProps) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [dob, setDob] = useState("");
  const [relationship, setRelationship] = useState(RELATIONSHIP_OPTIONS[0]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetForm = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setDob("");
    setRelationship(RELATIONSHIP_OPTIONS[0]);
  };

  const close = () => {
    onOpenChange(false);
    resetForm();
  };

  const canSubmit = firstName.trim() && lastName.trim() && dob && !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
    setIsSubmitting(true);
    const { error } = await createDependentApi({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim() || undefined,
      dob,
      relationship,
    });
    setIsSubmitting(false);

    if (error) {
      toastError(error);
      return;
    }

    onCreated();
    toastSuccess(`${fullName}'s profile has been created`);
    close();
  };

  return (
    <AppModal open={open} onOpenChange={close} width={560} className="rounded-3xl p-7">
      <div className="mb-5 flex items-start gap-4 pr-6">
        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-[#E4002B]/50">
          <Icon icon="lucide:plus" className="h-6 w-6 text-white" />
        </div>
        <div>
          <h2 className="text-lg font-extrabold text-[#0A2947]">Add Family Member</h2>
          <p className="mt-1 text-sm text-[#6B7280]">
            Create a fully-managed profile for someone who can't manage their own healthcare.
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <IconTextField icon="lucide:user-round" label="First Name" value={firstName} onChange={setFirstName} placeholder="Talha" required />
          <IconTextField icon="lucide:user-round" label="Last Name" value={lastName} onChange={setLastName} placeholder="Akhtar" required />
        </div>

        <IconTextField
          icon="lucide:mail"
          label="Email (optional)"
          value={email}
          onChange={setEmail}
          placeholder="name@example.com"
          type="email"
        />

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-[#374151]">
              Date of Birth<span className="text-[#E4002B]"> *</span>
            </label>
            <CalendarDateField value={dob} onChange={setDob} placeholder="Select date of birth" disableToday />
          </div>
          <IconSelectField icon="lucide:users" label="Relationship" value={relationship} onChange={setRelationship} options={RELATIONSHIP_OPTIONS} />
        </div>

        <div className="space-y-2 text-xs leading-relaxed text-[#9CA3AF]">
          <p>
            If {firstName.trim() || "they"} later signs up with this exact email, this profile and its full history can be
            transferred to their own account.
          </p>
          <p>You'll automatically get full management access — upload, view and edit — for this profile.</p>
        </div>
      </div>

      <div className="mt-6 flex justify-end gap-3">
        <Button
          variant="outline"
          onClick={close}
          disabled={isSubmitting}
          className="!rounded-lg !border-[#E4002B]/50 !px-5 !py-2.5 !text-sm !font-semibold !text-[#E4002B] hover:!border-[#E4002B]/50 hover:!bg-[#FFF2F4] hover:!text-[#E4002B]"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!canSubmit}
          loading={isSubmitting}
          className="!rounded-lg !bg-[#E4002B]/50 !px-5 !py-2.5 !text-sm !font-semibold !text-white hover:!bg-[#E4002B]/60"
        >
          {isSubmitting ? "Creating..." : "Create Profile"}
        </Button>
      </div>
    </AppModal>
  );
}
